import { AVATAR_SIZE_PX, MAX_AVATAR_BYTES } from "@catmail/shared";

/**
 * 고른 사진을 아바타 크기의 정사각형으로 줄인다.
 *
 * **줄이는 것을 여기서 하는 이유:** 서버에서 하려면 네이티브 의존성(sharp)이
 * 들어오고 도커 이미지가 두 배가 된다. 브라우저에는 이미 캔버스가 있고,
 * 폰에서 찍은 4MB짜리 사진을 그대로 올려보내는 것보다 여기서 40KB로 줄여
 * 보내는 편이 올리는 사람도 빠르다.
 *
 * **정사각형으로 자른다.** 아바타는 어디서나 정사각형이나 원으로 그려지므로
 * (`components/Avatar`) 비율이 다른 사진을 그대로 두면 화면에서 잘리는데,
 * 그 자르기는 CSS가 하므로 **고를 때 본 그림과 실제로 보이는 그림이 달라진다.**
 * 여기서 미리 잘라두면 미리보기가 곧 결과다.
 *
 * 자를 때는 **가운데를 채운다**(cover). 여백을 넣어 다 보여주는 방법도 있지만,
 * 얼굴 사진이 대부분이라 가운데가 남는 쪽이 맞고 여백은 아바타 색과 섞여
 * 지저분해진다.
 */

/** 사람이 고를 수 있는 것. 서버도 같은 넷만 받는다(storage/images.ts). */
export const AVATAR_ACCEPT = "image/png,image/jpeg,image/webp,image/gif";

export type AvatarImageError =
  | "not-image"
  | "too-large"
  | "decode-failed"
  | "encode-failed";

export class AvatarImageProblem extends Error {
  constructor(readonly reason: AvatarImageError) {
    super(reason);
  }
}

export type AvatarImage = {
  /** 서버로 보낼 알맹이. `data:` 접두사는 뺐다. */
  base64: string;
  /**
   * 미리보기에 그대로 쓰는 주소.
   *
   * 고른 파일이 아니라 **자르고 줄인 결과**다. 원본을 미리보기로 쓰면 고를 때
   * 본 그림과 저장되는 그림이 달라진다.
   */
  previewUrl: string;
};

/**
 * 그림 하나를 아바타로 만든다.
 *
 * `File`이 아니라 `Blob`을 받는다 — 고른 파일이든 고양이 API에서 받아 온
 * 바이트든(lib/catApi.ts) 여기서부터는 같은 길을 타야 한다.
 *
 * 실패는 던진다 — 부르는 쪽이 사람에게 보여줄 문구를 정한다. 여기서 문구를
 * 만들면 같은 사정을 화면마다 다르게 말하게 된다.
 */
export async function toAvatarImage(file: Blob): Promise<AvatarImage> {
  // 확장자가 아니라 브라우저가 읽은 형식을 본다. 어차피 서버가 바이트를 다시
  // 보지만(storage/images.ts), 여기서 걸러야 큰 파일을 올리다 튕기지 않는다.
  if (!file.type.startsWith("image/")) {
    throw new AvatarImageProblem("not-image");
  }

  const bitmap = await decode(file);

  try {
    const canvas = document.createElement("canvas");
    canvas.width = AVATAR_SIZE_PX;
    canvas.height = AVATAR_SIZE_PX;

    const context = canvas.getContext("2d");
    if (!context) throw new AvatarImageProblem("decode-failed");

    // 가운데를 정사각형으로 잘라 채운다.
    const side = Math.min(bitmap.width, bitmap.height);
    const left = (bitmap.width - side) / 2;
    const top = (bitmap.height - side) / 2;

    context.drawImage(
      bitmap,
      left,
      top,
      side,
      side,
      0,
      0,
      AVATAR_SIZE_PX,
      AVATAR_SIZE_PX,
    );

    // WebP가 같은 화질에 제일 작다. 못 만드는 브라우저에서는 조용히 PNG가
    // 나오는데, canvas는 그때도 성공을 돌려주므로 결과의 앞머리를 보고 판단하지
    // 않는다 — 서버가 넷 다 받으므로 무엇이 나오든 상관없다.
    const previewUrl = canvas.toDataURL("image/webp", 0.9);
    const base64 = previewUrl.split(",")[1] ?? "";
    if (base64.length === 0) throw new AvatarImageProblem("encode-failed");

    // base64는 원본의 4/3이다. 서버가 다시 재지만 여기서 막으면 헛걸음이 없다.
    if ((base64.length * 3) / 4 > MAX_AVATAR_BYTES) {
      throw new AvatarImageProblem("too-large");
    }

    return { base64, previewUrl };
  } finally {
    // ImageBitmap은 GC를 기다리지 않고 스스로 닫아야 메모리가 바로 풀린다.
    if ("close" in bitmap) bitmap.close();
  }
}

/**
 * 파일을 그림으로 푼다.
 *
 * `createImageBitmap`이 있으면 그걸 쓴다 — 디코딩이 워커로 빠져서 큰 사진에서
 * 화면이 멈추지 않는다. 없으면 `<img>`로 돌아간다.
 */
async function decode(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      throw new AvatarImageProblem("decode-failed");
    }
  }

  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new AvatarImageProblem("decode-failed"));
      image.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}
